import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { PurchaseLimitInput, PurchaseLimitSaved } from '../types/purchase-limit.types';
import {
  createPurchaseLimit,
  getPurchaseLimitImpact,
  getCustomerLimits,
  getPurchaseLimits,
  setPurchaseLimitStatus,
  updatePurchaseLimit,
} from './purchase-limits.api';
import { customerKeys } from './customers.queries';

export const purchaseLimitKeys = {
  all: ['purchase-limits'] as const,
  list: (params: object) => [...purchaseLimitKeys.all, 'list', params] as const,
  customer: (customerId: number) =>
    [...customerKeys.detail(customerId), 'purchase-limits'] as const,
};

export function usePurchaseLimits(countryId: number | undefined, includeInactive: boolean) {
  return useQuery({
    queryKey: purchaseLimitKeys.list({ countryId, includeInactive }),
    queryFn: ({ signal }) => getPurchaseLimits({ countryId, includeInactive }, signal),
  });
}

export function useCustomerLimits(customerId: number) {
  return useQuery({
    queryKey: purchaseLimitKeys.customer(customerId),
    queryFn: ({ signal }) => getCustomerLimits(customerId, signal),
  });
}

/** Tells staff who a saved limit already holds back — the "lowering below current use" warning. */
export function impactMessage(saved: PurchaseLimitSaved): string | null {
  const impact = saved.impact;
  if (!impact) return null;
  if (impact.customersAlreadyOver) {
    return `${impact.customersAlreadyOver} customer(s) are already above this limit and cannot buy until their usage falls.`;
  }
  if (impact.customerAlreadyOver) {
    return 'This customer is already above the limit and cannot buy until their usage falls.';
  }
  return null;
}

/** Dry run before saving: who the limit would already hold back. Changes nothing. */
export function usePurchaseLimitImpact() {
  return useMutation({
    mutationFn: (input: PurchaseLimitInput) => getPurchaseLimitImpact(input),
  });
}

function useInvalidateLimits() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: purchaseLimitKeys.all });
    void queryClient.invalidateQueries({ queryKey: customerKeys.all });
  };
}

function announce(saved: PurchaseLimitSaved, text: string) {
  const warning = impactMessage(saved);
  if (warning) toast.warning(`${text} ${warning}`);
  else toast.success(text);
}

export function useCreatePurchaseLimit() {
  const invalidate = useInvalidateLimits();
  return useMutation({
    mutationFn: (input: PurchaseLimitInput) => createPurchaseLimit(input),
    onSuccess: (saved) => {
      announce(saved, 'Limit created.');
      invalidate();
    },
  });
}

export function useUpdatePurchaseLimit() {
  const invalidate = useInvalidateLimits();
  return useMutation({
    mutationFn: (input: { id: number; maxAmount: number; periodDays: number; reason: string }) =>
      updatePurchaseLimit(input.id, input),
    onSuccess: (saved) => {
      announce(saved, 'Limit saved.');
      invalidate();
    },
  });
}

export function useSetPurchaseLimitStatus() {
  const invalidate = useInvalidateLimits();
  return useMutation({
    mutationFn: (input: { id: number; isActive: boolean; reason: string }) =>
      setPurchaseLimitStatus(input.id, input.isActive, input.reason),
    onSuccess: (_, input) => {
      toast.success(input.isActive ? 'Limit switched on.' : 'Limit switched off.');
      invalidate();
    },
  });
}
